import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivation } from '@context/MobileOne';
import { useStaticContent } from '@context/StaticContent';
import { useA11y } from '@context/Utils';
import { usePayment } from '@context/Payment';
import paymentService from '@services/payment';
import { getBgColors } from '@utils/teaserGradient';
import {
  usePaymentFlow,
  PAYMENT_STATUS,
  PaymentCardFields,
  PaymentOption,
  PaymentOptionsList,
  ApplePayButton,
  GooglePayButton,
  PayPalButton
} from '@modules/Payment';
import {
  ButtonPrimary,
  ButtonCancelActivation,
  ScrollToTop,
  Ta11yText,
  Notification,
  Chip,
  CardInfoboxChip,
  Meta,
  ApiError,
  SectionAmount,
  CardSupport
} from '@core/index';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import {
  appButtonType,
  appAlert,
  appRoute,
  appIconsPath,
  appInfoCardSize,
  PAYMENT_METHODS,
  CREDIT_CARD_SUBTYPES,
  appTariffs
} from '@utils/globalConstant';

export function MpsAutoTopupPaymentMethod() {
  const cardFieldsApiRef = useRef(null);
  const requestIdRef = useRef(null);
  const navigate = useNavigate();

  // Contexts
  const { tA11yTranslate } = useA11y();
  const {
    tariffActivationForm,
    phoneNumberActivationForm,
    personalDataForm,
    withAutoTopup,
    withMNP,
    legitimationMethods,
    bookableTariffs,
    bookableTariffsEtcOne
  } = useActivation();
  const { campaignContentData, staticContentData } = useStaticContent();
  const { mpsActivationTopupPayment, setActivationMpsTopupPayment } = usePayment();

  // States
  const [amount, setAmount] = useState('');
  const [fieldsReady, setFieldsReady] = useState(false);
  const [clientReady, setClientReady] = useState(false);
  const [cardFieldsKey, setCardFieldsKey] = useState(Date.now());
  const [creditCardHint, setCreditCardHint] = useState([]);
  const [availableMethods, setAvailableMethods] = useState([]);
  const [paymentMethodsLoadError, setPaymentMethodsLoadError] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [noPaymentMethodSelectedError, setNoPaymentMethodSelectedError] = useState('');
  const [uiSelectedMethod, setUiSelectedMethod] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [creditCardType, setCreditCardType] = useState(null);

  // Campaign-driven infobox and badge from static content
  const infoboxData = campaignContentData?.campaignRegTopupAutoPaymentInfobox?.[0];
  const badgeData = campaignContentData?.campaignRegTopupAutoPaymentBadge?.[0];
  const { bgColor1: infoboxBg1, bgColor2: infoboxBg2 } = getBgColors(
    infoboxData?.cardBgColorLight || ''
  );

  // let pageTitle = tA11yTranslate('ek_reg_autotopup_pymnt_mthd_hdl1')?.content;
  let autoTopupPaymentMethodPageHeadingLabel = '';
  if (
    tariffActivationForm.chosenTariffId === appTariffs.TALK ||
    tariffActivationForm.chosenTariffId === 0
  ) {
    autoTopupPaymentMethodPageHeadingLabel = tA11yTranslate(
      'ek_reg_autotopup_pymnt_mthd_hdl4'
    )?.content;
  } else {
    autoTopupPaymentMethodPageHeadingLabel = tA11yTranslate(
      'ek_reg_autotopup_pymnt_mthd_hdl1'
    )?.content;
  }

  // Payment Flow Hook (must be called before buildPaymentOptions)
  const {
    status,
    error: paymentInitializationError,
    selectedMethod,
    setSelectedMethod,
    selectPaymentMethod,
    initializeCardFields,
    processPayment,
    isCardFormValid,
    canUseApplePay,
    canUseGooglePay
  } = usePaymentFlow({
    flow: 'activation'
  });

  const staticPaymentMethods = staticContentData?.mpsPaymentMethods || [];
  const paymentOptions = staticPaymentMethods.map((item) => {
    let show = availableMethods.length === 0 || availableMethods.includes(item.value);

    // If creditcard is available, show all subtypes
    if (
      availableMethods.includes(PAYMENT_METHODS.CREDIT_CARD) &&
      (item.value === CREDIT_CARD_SUBTYPES.VISA ||
        item.value === CREDIT_CARD_SUBTYPES.MASTERCARD ||
        item.value === CREDIT_CARD_SUBTYPES.AMEX)
    ) {
      show = true;
    }

    return {
      method: item.value,
      label: item.label,
      iconPath: item.imageSource || undefined,
      show,
      requiresCardForm: item.requiresCardForm,
      matchValue: item.matchValue,
      sortOrder: item.sortOrder
    };
  });
  const filteredPaymentOptions = paymentOptions.filter((opt) => opt.show);

  // Split credit cards
  const visaMastercardOptions = filteredPaymentOptions.filter(
    (opt) =>
      opt.method === CREDIT_CARD_SUBTYPES.VISA || opt.method === CREDIT_CARD_SUBTYPES.MASTERCARD
  );
  const amexOption = filteredPaymentOptions.find((opt) => opt.method === CREDIT_CARD_SUBTYPES.AMEX);
  const combinedCreditCardLogo = filteredPaymentOptions.find(
    (opt) => opt.method === 'creditcard'
  )?.iconPath;

  // Initialize payment step
  useEffect(() => {
    const currentRequestId = Math.random().toString(36).substring(7);
    requestIdRef.current = currentRequestId;

    const initPaymentStep = async () => {
      try {
        // Hydrate amount from context
        // console.log('🔍 Payment Method - Debug:', {
        //   'mpsActivationTopupPayment.amount': mpsActivationTopupPayment.amount,
        //   'tariffActivationForm.chosenTariffId': tariffActivationForm.chosenTariffId,
        //   'tariffActivationForm.tariffAmount': tariffActivationForm.tariffAmount
        // });

        // For tariffs with monthly fee (amount > 0), use tariff amount; for TALK (amount = 0), use selected amount
        const resolvedAmount =
          tariffActivationForm.tariffAmount > 0
            ? tariffActivationForm.tariffAmount
            : mpsActivationTopupPayment.amount;

        // console.log('💰 Resolved amount:', resolvedAmount, 'Type:', typeof resolvedAmount);

        setAmount(resolvedAmount);
        if (tariffActivationForm.tariffAmount > 0) {
          setActivationMpsTopupPayment((prev) => ({
            ...prev,
            amount: resolvedAmount
          }));
        }

        // Fetch available payment methods with device capability flags
        setPaymentMethodsLoadError(null);
        const result = await paymentService.getPaymentMethods({
          type: 'recurring',
          applepay: canUseApplePay,
          googlepay: canUseGooglePay
        });

        // Check if this request is still current (race condition prevention)
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        setAvailableMethods(result.methods || []);
        setCreditCardHint(result.creditcard_hint || []);
        setUiSelectedMethod('');
      } catch (err) {
        setPaymentMethodsLoadError(err.message || 'Failed to load payment methods');
      }
    };

    initPaymentStep();
  }, [canUseApplePay, canUseGooglePay]);

  // Watch for Braintree SDK ready
  useEffect(() => {
    // console.log('🔄 Payment status changed:', status);
    if (status === PAYMENT_STATUS.READY) {
      // console.log('✅ Braintree SDK ready, setting clientReady to true');
      setClientReady(true);
    }
  }, [status]);

  // Handlers
  const selectPaymentOption = (method) => {
    setUiSelectedMethod(method);
    setSelectedMethod(method);
    setActivationMpsTopupPayment((prev) => ({ ...prev, paymentMethod: method }));
    // Clear validation error when user selects a payment method
    setNoPaymentMethodSelectedError('');
    setSubmitAttempted(false);
  };

  const handleMethodSelect = async (method) => {
    setFieldsReady(false);
    setClientReady(false);

    selectPaymentOption(method);

    if (method !== PAYMENT_METHODS.CREDIT_CARD) {
      if (cardFieldsApiRef.current) {
        try {
          await cardFieldsApiRef.current.teardown?.();
        } catch (err) {
          // console.error('Card fields teardown failed:', err);
        }
        cardFieldsApiRef.current = null;
      }
      setCreditCardType(null);
      setFieldsReady(false);
    }

    try {
      await selectPaymentMethod(method);
    } catch (err) {
      // console.error('Payment method initialization failed:', err);
    }
  };

  const handleCreditCardSelect = async (subtype) => {
    // console.log('💳 handleCreditCardSelect called:', subtype);
    setFieldsReady(false);
    setClientReady(false);

    // Teardown previous card fields
    if (cardFieldsApiRef.current) {
      try {
        await cardFieldsApiRef.current.teardown?.();
      } catch (err) {
        // console.error('Card fields teardown failed:', err);
      }
      cardFieldsApiRef.current = null;
    }

    setCreditCardType(subtype);
    setCardFieldsKey(Date.now());
    selectPaymentOption(PAYMENT_METHODS.CREDIT_CARD);

    try {
      // console.log('🔄 Calling selectPaymentMethod for CREDIT_CARD');
      await selectPaymentMethod(PAYMENT_METHODS.CREDIT_CARD);
      // console.log('✅ selectPaymentMethod completed');
    } catch (err) {
      // console.error('Credit card initialization failed:', err);
    }
  };

  // Store activation data to sessionStorage (critical for PayPal redirect recovery)
  const storeActivationDataToSessionStorage = () => {
    try {
      sessionStorage.setItem('mps_activation_tariff', JSON.stringify(tariffActivationForm));
      sessionStorage.setItem('mps_activation_phone', JSON.stringify(phoneNumberActivationForm));
      sessionStorage.setItem('mps_activation_personal', JSON.stringify(personalDataForm));
      sessionStorage.setItem('mps_activation_withAutoTopup', JSON.stringify(withAutoTopup));
      sessionStorage.setItem('mps_activation_withMnp', JSON.stringify(withMNP));
      sessionStorage.setItem('mps_bookable_tariffs', JSON.stringify(bookableTariffs));
      sessionStorage.setItem('mps_bookable_tariffs_etc_one', JSON.stringify(bookableTariffsEtcOne));
      sessionStorage.setItem('mps_legitimation_methods', JSON.stringify(legitimationMethods));
    } catch (err) {
      // console.error('Failed to store activation data:', err);
    }
  };

  const handlePayment = async () => {
    const isCreditCard = selectedMethod === PAYMENT_METHODS.CREDIT_CARD;

    // console.log('🚀 handlePayment called:', {
    //   selectedMethod,
    //   isCreditCard,
    //   fieldsReady,
    //   isCardFormValid,
    //   'cardFieldsApiRef.current': cardFieldsApiRef.current
    // });

    if (isCreditCard) {
      const api = cardFieldsApiRef.current;
      const childValid =
        typeof api?.isValid === 'function' ? api.isValid() : Boolean(isCardFormValid);

      // console.log('💳 Credit card validation:', {
      //   fieldsReady,
      //   childValid,
      //   'api.isValid exists': typeof api?.isValid === 'function',
      //   'api.isValid()': typeof api?.isValid === 'function' ? api.isValid() : 'N/A',
      //   isCardFormValid
      // });

      if (!fieldsReady || !childValid) {
        // console.log('❌ Validation failed, triggering validation');
        api?.triggerValidation?.();
        setSubmitAttempted(true);
        return;
      }
    }

    // REMOVE DEV FALLBACK: Use test MSISDN if not in context (for development shortcuts)
    // const msisdnToUse = phoneNumberActivationForm?.msisdn || '01798897802';
    const msisdnToUse = phoneNumberActivationForm?.msisdn;

    // console.log('📞 MSISDN check:', msisdnToUse, '(using dev fallback:', !phoneNumberActivationForm?.msisdn, ')');

    // TODO: Uncomment for production - validate MSISDN exists
    if (!msisdnToUse) {
      setNoPaymentMethodSelectedError(
        tA11yTranslate('ek_global_pymnt_mthd_err')?.content || 'Please select a payment method'
      );
      return;
    }

    // console.log('✅ All validations passed, processing payment...');

    // REMOVE - Google Pay debug logging
    // if (selectedMethod === PAYMENT_METHODS.GOOGLE_PAY) {
    //   console.log('🔍 [GooglePay - Activation] Payment initiated with config:', {
    //     merchantId: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID,
    //     environment: process.env.REACT_APP_GOOGLE_PAY_ENV,
    //     braintreeEnv: process.env.REACT_APP_BRAINTREE_ENV,
    //     amount,
    //     currency: 'EUR',
    //     msisdn: msisdnToUse,
    //     flow: 'activation',
    //     canUseGooglePay,
    //     selectedMethod
    //   });
    // }

    try {
      setIsLoading(true);

      // CRITICAL: Store activation data BEFORE processPayment (PayPal redirects immediately)
      storeActivationDataToSessionStorage();

      // Clear stale sessionStorage
      sessionStorage.removeItem('mps_payment_result');
      sessionStorage.removeItem('mps_payment_flow');

      // Set fresh flow and payment data
      sessionStorage.setItem('mps_payment_flow', 'activation');
      sessionStorage.setItem('mps_payment_amount', amount);
      sessionStorage.setItem('mps_payment_method', selectedMethod);

      const result = await processPayment({
        amount,
        currency: 'EUR',
        msisdn: msisdnToUse,
        useStored: false
      });

      // PayPal redirect - browser navigates away
      if (result?.requiresRedirect) {
        return;
      }

      // User cancelled (Apple Pay/Google Pay sheet closed)
      if (result?.cancelled) {
        setIsLoading(false);
        setActivationMpsTopupPayment((prev) => ({
          ...prev,
          paymentStatus: 'cancelled'
        }));
        return;
      }

      // Error result
      if (result?.error) {
        const lookupKey = result?.lookup_key;

        setActivationMpsTopupPayment((prev) => ({
          ...prev,
          lookup_key: lookupKey,
          paymentStatus: 'error',
          paymentResult: result
        }));

        sessionStorage.setItem(
          'mps_payment_result',
          JSON.stringify({
            lookup_key: lookupKey,
            paymentStatus: 'error',
            amount,
            paymentMethod: selectedMethod
          })
        );

        setIsLoading(false);
        navigate(appRoute.MPS_TOPUP_RESULT);
        return;
      }

      // Success
      const lookupKey = result?.lookup_key;

      setActivationMpsTopupPayment((prev) => ({
        ...prev,
        lookup_key: lookupKey,
        paymentStatus: 'completed',
        paymentResult: result
      }));

      sessionStorage.setItem(
        'mps_payment_result',
        JSON.stringify({
          lookup_key: lookupKey,
          paymentStatus: 'completed',
          amount,
          paymentMethod: selectedMethod
        })
      );

      setIsLoading(false);
      navigate(appRoute.MPS_TOPUP_RESULT);
    } catch (err) {
      // Check if user cancelled 3DS verification
      if (err?.cancelled) {
        setIsLoading(false);
        return;
      }

      // console.error('Payment processing failed:', err);
      setIsLoading(false);
    }
  };

  const handlePayClick = () => {
    // console.log('👆 handlePayClick called, selectedMethod:', selectedMethod);

    if (!selectedMethod) {
      setSubmitAttempted(true);
      setNoPaymentMethodSelectedError(
        tA11yTranslate('ek_global_pymnt_mthd_err')?.content || 'Please select a payment method'
      );
      return;
    }

    setNoPaymentMethodSelectedError(null);
    handlePayment();
  };

  // Render branded payment button
  const renderBrandedButton = () => {
    if (!selectedMethod || selectedMethod === PAYMENT_METHODS.CREDIT_CARD) {
      return null;
    }

    if (selectedMethod === PAYMENT_METHODS.APPLE_PAY) {
      if (!window.ApplePaySession) {
        return null;
      }
      return (
        <ApplePayButton
          onClick={handlePayClick}
          disabled={status !== PAYMENT_STATUS.READY}
          isLoading={status === PAYMENT_STATUS.PROCESSING || isLoading}
        />
      );
    }

    if (selectedMethod === PAYMENT_METHODS.PAYPAL) {
      return (
        <PayPalButton
          onClick={handlePayClick}
          disabled={status !== PAYMENT_STATUS.READY}
          isLoading={status === PAYMENT_STATUS.PROCESSING || isLoading}
        />
      );
    }

    if (selectedMethod === PAYMENT_METHODS.GOOGLE_PAY) {
      return (
        <GooglePayButton
          onClick={handlePayClick}
          disabled={status !== PAYMENT_STATUS.READY}
          isLoading={status === PAYMENT_STATUS.PROCESSING || isLoading}
        />
      );
    }

    return null;
  };

  return (
    <ScrollToTop>
      <AppPageContentWrapper>
        {/* Amount Display */}
        <SectionAmount
          label={autoTopupPaymentMethodPageHeadingLabel}
          value={amount}
          className="mb-24"
        />

        {paymentMethodsLoadError && (
          <div className="mt-16">
            <ApiError
              message={paymentMethodsLoadError}
              isGenericApiError={false}
              messageIsPlainText
            />
          </div>
        )}

        {!paymentMethodsLoadError && (
          <>
            {/* Campaign gift info box — driven by campaignRegTopupAutoPaymentInfobox */}
            {infoboxData && (
              <div className="mb-32">
                <CardInfoboxChip
                  cardSize={appInfoCardSize.LARGE}
                  chipIcon={infoboxData.chipIconWeb || 'GIFT'}
                  chipLabel={infoboxData.chipTitle}
                  chipTitleColor={infoboxData.chipTitleColorLight}
                  text={infoboxData.label}
                  // new props for KIK campaings
                  bgStyle={infoboxData.cardStyle}
                  linearGradient={infoboxData.linearGradient || 'leftToRight'}
                  bgColor1={infoboxBg1}
                  bgColor2={infoboxBg2}
                />
              </div>
            )}

            <div className="mb-24">
              <Ta11yText
                textContent={tA11yTranslate('ek_global_choose_pymnt_mthd_hdl1')}
                tag="h3"
                className="ek-copy-16-medium"
              />
            </div>

            <div className="mt-0">
              {/* Highlight badge — driven by campaignRegTopupAutoPaymentBadge */}
              {badgeData && (
                <div className="mb-12">
                  <Chip
                    label={badgeData.chipTitle}
                    icon={
                      badgeData.chipIconWeb
                        ? appIconsPath[badgeData.chipIconWeb] || badgeData.chipIconWeb || 'GIFT'
                        : null
                    }
                    chipStyle={badgeData.chipBgColorLight}
                  />
                </div>
              )}
            </div>

            <div className="payment-methods-container">
              {/* Non-credit card payment methods */}
              <PaymentOptionsList
                options={filteredPaymentOptions.filter(
                  (opt) =>
                    opt.method !== PAYMENT_METHODS.CREDIT_CARD &&
                    opt.method !== CREDIT_CARD_SUBTYPES.VISA &&
                    opt.method !== CREDIT_CARD_SUBTYPES.MASTERCARD &&
                    opt.method !== CREDIT_CARD_SUBTYPES.AMEX
                )}
                selectedMethod={uiSelectedMethod}
                onSelect={handleMethodSelect}
                initializeCardFields={initializeCardFields}
                cardFieldsKey={cardFieldsKey}
                setFieldsReady={setFieldsReady}
                cardFieldsApiRef={cardFieldsApiRef}
                creditCardHint={creditCardHint}
                submitAttempted={submitAttempted}
                clientReady={clientReady}
                creditCardType={creditCardType}
              />

              {/* VISA + Mastercard combined */}
              {visaMastercardOptions.length > 0 && (
                <PaymentOption
                  method={CREDIT_CARD_SUBTYPES.VISA}
                  label={visaMastercardOptions.map((opt) => opt.label).join(' / ')}
                  iconPath={combinedCreditCardLogo}
                  isSelected={
                    uiSelectedMethod === PAYMENT_METHODS.CREDIT_CARD &&
                    (creditCardType === CREDIT_CARD_SUBTYPES.VISA ||
                      creditCardType === CREDIT_CARD_SUBTYPES.MASTERCARD)
                  }
                  onSelect={() => handleCreditCardSelect(CREDIT_CARD_SUBTYPES.VISA)}>
                  {uiSelectedMethod === PAYMENT_METHODS.CREDIT_CARD && clientReady && (
                    <PaymentCardFields
                      key={`${creditCardType}-${cardFieldsKey}`}
                      initializeFields={initializeCardFields}
                      onReady={(api) => {
                        // console.log('✅ Card fields ready (VISA/MC):', api);
                        setFieldsReady(true);
                        cardFieldsApiRef.current = api;
                      }}
                      onError={() => console.error('Card fields error')}
                      fieldsHint={creditCardHint}
                      submitAttempted={submitAttempted}
                      creditCardType={creditCardType}
                    />
                  )}
                </PaymentOption>
              )}

              {/* AMEX */}
              {amexOption && (
                <PaymentOption
                  method={CREDIT_CARD_SUBTYPES.AMEX}
                  label={amexOption.label}
                  iconPath={amexOption.iconPath}
                  isSelected={
                    uiSelectedMethod === PAYMENT_METHODS.CREDIT_CARD &&
                    creditCardType === CREDIT_CARD_SUBTYPES.AMEX
                  }
                  onSelect={() => handleCreditCardSelect(CREDIT_CARD_SUBTYPES.AMEX)}>
                  {uiSelectedMethod === PAYMENT_METHODS.CREDIT_CARD && clientReady && (
                    <PaymentCardFields
                      key={`${creditCardType}-${cardFieldsKey}`}
                      initializeFields={initializeCardFields}
                      onReady={(api) => {
                        // console.log('✅ Card fields ready (AMEX):', api);
                        setFieldsReady(true);
                        cardFieldsApiRef.current = api;
                      }}
                      onError={() => console.error('Card fields error')}
                      fieldsHint={creditCardHint}
                      submitAttempted={submitAttempted}
                      creditCardType={creditCardType}
                    />
                  )}
                </PaymentOption>
              )}
            </div>

            {/* Show errors below payment methods */}
            {paymentInitializationError && (
              <div className="mt-16">
                <ApiError isGenericApiError={false} messageIsPlainText />
              </div>
            )}

            {submitAttempted && noPaymentMethodSelectedError && (
              <div className="mt16">
                <Notification type={appAlert.ERROR} message={noPaymentMethodSelectedError} />
              </div>
            )}

            <div className="mt-48">
              {!uiSelectedMethod || uiSelectedMethod === PAYMENT_METHODS.CREDIT_CARD ? (
                <ButtonPrimary
                  buttonType={appButtonType.PRIMARY.DEFAULT}
                  buttonContent={tA11yTranslate('ek_reg_autotopup_pymnt_mthd_info_btn1')}
                  onClick={handlePayClick}
                  isLoading={isLoading}
                />
              ) : (
                <div style={{ width: '311px' }} className="mx-auto">
                  {renderBrandedButton()}
                </div>
              )}
            </div>

            {badgeData?.footnote && (
              <div className="mt-48">
                <p className="ek-labeltext-12-medium">{badgeData.footnote}</p>
              </div>
            )}
          </>
        )}

        <div className="row mt-48">
          <CardSupport />
        </div>

        <div className="d-flex justify-content-center mt-32">
          <ButtonCancelActivation />
        </div>
      </AppPageContentWrapper>
    </ScrollToTop>
  );
}

export default MpsAutoTopupPaymentMethod;
